import React from 'react';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowUpCircleFill, ArrowDownCircleFill } from 'react-bootstrap-icons';

// This is a Bootstrap version of the SubscriptionDetail component
// It replaces any Material UI components with React Bootstrap equivalents

const SubscriptionDetail = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [showCancelModal, setShowCancelModal] = React.useState(false);

  // Fetch subscription data
  React.useEffect(() => {
    // Mock data for demonstration
    setTimeout(() => {
      setSubscription({
        id: 123,
        plan_name: 'Gold Standard',
        status: 'active',
        metal_type: 'gold',
        monthly_amount: 1,
        weight_unit: 'oz',
        monthly_cost: 1850,
        next_delivery_date: '2023-06-15',
        start_date: '2023-01-15',
        total_weight: 5,
        transactions: [
          { id: 1, date: '2023-05-15', amount: 1850, status: 'completed' },
          { id: 2, date: '2023-04-15', amount: 1850, status: 'completed' },
          { id: 3, date: '2023-03-15', amount: 1850, status: 'completed' },
        ],
        deliveries: [
          { id: 101, expected_date: '2023-05-20', amount: 1, status: 'delivered' },
          { id: 102, expected_date: '2023-04-20', amount: 1, status: 'delivered' },
          { id: 103, expected_date: '2023-03-20', amount: 1, status: 'delivered' },
        ]
      });
      setLoading(false);
    }, 1000);
  }, []);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Handle cancel subscription
  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  // Get metal price change indicator
  const getPriceChange = (current, previous) => {
    if (!previous) return null;

    const change = ((current - previous) / previous) * 100;
    const isPositive = change >= 0;

    return (
      <div className={`d-flex align-items-center ${isPositive ? 'text-success' : 'text-danger'}`}>
        {isPositive ? <ArrowUpCircleFill className="me-1" /> : <ArrowDownCircleFill className="me-1" />}
        <span>{Math.abs(change).toFixed(2)}%</span>
      </div>
    );
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="d-flex justify-content-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="fw-bold">{subscription.plan_name}</h2>
              <p className="text-muted">Subscription #{subscription.id}</p>
            </div>
            <div>
              <Button
                variant="outline-secondary"
                className="me-2"
                onClick={() => navigate('/subscriptions')}
              >
                Back to Subscriptions
              </Button>
              <Button
                variant="outline-danger"
                onClick={handleCancelSubscription}
              >
                Cancel Subscription
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <Row>
                <Col md={3} className="mb-3 mb-md-0">
                  <h6 className="text-muted mb-1">Status</h6>
                  <div className="d-flex align-items-center">
                    <div className={`rounded-circle me-2 bg-success`} style={{ width: 10, height: 10 }}></div>
                    <span className="text-capitalize fw-bold">{subscription.status}</span>
                  </div>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <h6 className="text-muted mb-1">Metal Type</h6>
                  <p className="mb-0 text-capitalize fw-bold">{subscription.metal_type}</p>
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <h6 className="text-muted mb-1">Monthly Amount</h6>
                  <p className="mb-0 fw-bold">{subscription.monthly_amount} {subscription.weight_unit}</p>
                </Col>
                <Col md={3}>
                  <h6 className="text-muted mb-1">Monthly Cost</h6>
                  <p className="mb-0 fw-bold">{formatCurrency(subscription.monthly_cost)}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-4">
        <Col md={6}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-white py-3">
              <h5 className="mb-0">Subscription Details</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">Start Date:</span>
                  <span>{new Date(subscription.start_date).toLocaleDateString()}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">Next Delivery:</span>
                  <span>{new Date(subscription.next_delivery_date).toLocaleDateString()}</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">Total Accumulated:</span>
                  <span>{subscription.total_weight} {subscription.weight_unit}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Total Value:</span>
                  <span>{formatCurrency(subscription.total_weight * subscription.monthly_cost)}</span>
                </div>
              </div>

              <div className="d-grid gap-2 mt-4">
                <Button
                  variant="outline-primary"
                  onClick={() => navigate(`/subscriptions/${subscription.id}/edit`)}
                >
                  Edit Subscription
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate(`/subscriptions/${subscription.id}/history`)}
                >
                  View Full History
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="bg-white py-3">
              <h5 className="mb-0">Payment Schedule</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">Payment Method:</span>
                  <span>Visa ending in 4242</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">Billing Cycle:</span>
                  <span>Monthly (15th)</span>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">Next Payment:</span>
                  <span>{new Date(subscription.next_delivery_date).toLocaleDateString()}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Amount:</span>
                  <span>{formatCurrency(subscription.monthly_cost)}</span>
                </div>
              </div>

              <div className="d-grid gap-2 mt-4">
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/account/payment-methods')}
                >
                  Update Payment Method
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
              <h5 className="mb-0">Recent Transactions</h5>
              <Button
                variant="link"
                className="p-0 text-decoration-none"
                onClick={() => navigate('/transactions')}
              >
                View All
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subscription.transactions.map((transaction) => (
                    <tr key={transaction.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/transactions/${transaction.id}`)}>
                      <td>{new Date(transaction.date).toLocaleDateString()}</td>
                      <td>{formatCurrency(transaction.amount)}</td>
                      <td>
                        <span className={`badge bg-${transaction.status === 'completed' ? 'success' : 'warning'} rounded-pill`}>
                          {transaction.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
              <h5 className="mb-0">Recent Deliveries</h5>
              <Button
                variant="link"
                className="p-0 text-decoration-none"
                onClick={() => navigate('/deliveries')}
              >
                View All
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subscription.deliveries.map((delivery) => (
                    <tr key={delivery.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/deliveries/${delivery.id}`)}>
                      <td>{new Date(delivery.expected_date).toLocaleDateString()}</td>
                      <td>{delivery.amount} {subscription.weight_unit}</td>
                      <td>
                        <span className={`badge bg-${delivery.status === 'delivered' ? 'success' : 'info'} rounded-pill`}>
                          {delivery.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Cancel Subscription Modal would be implemented here using React Bootstrap Modal component */}
    </Container>
  );
};

export default SubscriptionDetail;
